import React from "react";
import { Link } from "react-router-dom";
import {
  ClipboardCheck,
  MessageSquare,
  Compass,
  FileSignature,
  Send,
  Flag,
  Receipt,
  BadgeCheck,
  Landmark,
  Coins,
  ArrowRight,
} from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import CtaBand from "../components/CtaBand";
import { OUTCOME_DISCLAIMER, WHO_DOES_WHAT } from "../data/legal";

const steps = [
  {
    icon: ClipboardCheck,
    title: "Tell us about your situation",
    body: "Tell us where you are up to through our quick intake or a call. We get a clear picture of your situation and what you will need. It is free and there is no obligation.",
  },
  {
    icon: MessageSquare,
    title: "How we help",
    body: "We sit down with you, properly, to understand your goals and gather what your file needs. We organise your documents into a clean, checked, submission-ready file.",
  },
  {
    icon: Compass,
    title: "Matched with a registered agent",
    body: "We match you with a registered migration agent we have vetted. The agent assesses your eligibility, recommends the visa, and gives the regulated advice. That judgement is theirs, not ours.",
  },
  {
    icon: FileSignature,
    title: "Fees and agreement upfront",
    body: "Before any work begins, our support fee is set out in writing. The registered migration agent provides their own written agreement and the Consumer Guide, with their fee set out separately.",
  },
  {
    icon: Send,
    title: "Preparation and support",
    body: "We keep your documentation complete and ready while the registered migration agent lodges your application and deals with the Department of Home Affairs.",
  },
  {
    icon: Flag,
    title: "We keep it moving",
    body: "We stay your single point of contact, hold the matter to agreed milestones, and keep you informed so nothing stalls and no one goes quiet on you. The Department decides the outcome.",
  },
];

const fees = [
  {
    icon: Receipt,
    title: "Our support fee",
    body: "What you pay us for preparation, matching, and coordination. It is set out in writing and agreed before we start. This is our only charge to you.",
  },
  {
    icon: BadgeCheck,
    title: "The registered migration agent's fee",
    body: "What the registered migration agent charges for the regulated advice and lodgement. It is their fee, set out in their own agreement, and it is separate from ours.",
  },
  {
    icon: Landmark,
    title: "Government charges",
    body: "Visa Application Charges are set by the Australian Government and paid to the Department of Home Affairs. They are separate again and are not our revenue.",
  },
  {
    icon: Coins,
    title: "Third-party costs",
    body: "Skills assessments, police checks, health examinations, and translations are charged by those providers. We flag these early so your budget is complete.",
  },
];

export default function HowItWorks() {
  return (
    <>
      <SEO
        title="How it works"
        description="How we work with you: we prepare your documentation and match you with a registered migration agent for the regulated advice and lodgement, then keep your matter moving. Fees in writing upfront."
        path="/how-it-works/"
      />
      <Breadcrumbs
        items={[{ label: "Home", to: "/" }, { label: "How it works" }]}
      />

      {/* Intro */}
      <section className="container-page py-12">
        <p className="eyebrow">How it works</p>
        <h1 className="mt-3 max-w-3xl text-4xl font-bold text-acblue sm:text-5xl">
          A clear, three-part process, with nothing hidden
        </h1>
        <p className="mt-5 max-w-2xl text-lg text-acBlack/70">
          You should never feel in the dark about your own application. Here is
          exactly how we work with you, who does what, and how the fees are
          structured.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link to="/eligibility-quiz/" className="btn-primary">
            Start the eligibility check
            <ArrowRight className="h-4 w-4" />
          </Link>
          <Link to="/book-consultation/" className="btn-ghost">
            Book a consultation
          </Link>
        </div>
      </section>

      {/* Three-party split */}
      <section className="bg-acblue py-14 text-white">
        <div className="container-page">
          <div className="mx-auto max-w-2xl text-center">
            <p className="text-xs font-semibold uppercase tracking-widest text-acGold">
              {WHO_DOES_WHAT.heading}
            </p>
            <h2 className="mt-3 text-3xl font-bold sm:text-4xl">
              Three parties, one clear line each
            </h2>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            <div className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-lg font-semibold text-acGold">We do</h3>
              <p className="mt-2 text-sm text-white/80">{WHO_DOES_WHAT.us}</p>
            </div>
            <div className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-lg font-semibold text-acGold">
                A registered migration agent does
              </h3>
              <p className="mt-2 text-sm text-white/80">{WHO_DOES_WHAT.agent}</p>
            </div>
            <div className="rounded-3xl bg-white/5 p-6 ring-1 ring-white/10">
              <h3 className="text-lg font-semibold text-acGold">
                The Department decides
              </h3>
              <p className="mt-2 text-sm text-white/80">{WHO_DOES_WHAT.dept}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Steps */}
      <section className="bg-acBg py-16">
        <div className="container-page">
          <h2 className="text-3xl font-bold text-acblue sm:text-4xl">
            The journey, step by step
          </h2>
          <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {steps.map((s, i) => (
              <div key={s.title} className="card">
                <div className="flex items-center justify-between">
                  <s.icon className="h-9 w-9 text-acAccent" />
                  <span className="text-3xl font-bold text-acNavySoft">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="mt-4 text-lg font-semibold text-acblue">
                  {s.title}
                </h3>
                <p className="mt-2 text-sm text-acBlack/70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fees structure */}
      <section className="container-page py-16">
        <div className="mx-auto max-w-2xl text-center">
          <p className="eyebrow">How fees work</p>
          <h2 className="mt-3 text-3xl font-bold text-acblue sm:text-4xl">
            Four separate costs, always kept separate
          </h2>
          <p className="mt-4 text-acBlack/70">
            Hidden and bundled fees are one of the biggest complaints in this
            industry. We break your costs into four clear parts so you always
            know what is ours, what is the registered migration agent's, what is
            the government's, and what is a third party's.
          </p>
        </div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {fees.map((f) => (
            <div key={f.title} className="card">
              <f.icon className="h-9 w-9 text-acAccent" />
              <h3 className="mt-4 text-lg font-semibold text-acblue">
                {f.title}
              </h3>
              <p className="mt-2 text-sm text-acBlack/70">{f.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-8 rounded-2xl bg-acNavySoft/50 p-5 text-sm text-acBlack/75">
          Our support fee is agreed in writing before any work begins. The
          registered migration agent provides their own written agreement and
          the Consumer Guide, so every cost is clear and agreed in advance. {" "}
          {OUTCOME_DISCLAIMER}
        </div>
      </section>

      <CtaBand />
    </>
  );
}
